import { InstallationTelemetry } from '@core/models/installation.model';
import {
  DevicePvForecast,
  PvForecastEntry,
  PvForecastInterval,
} from '@core/models/pv-forecast.model';
import moment from 'moment-timezone';

export interface PvForecastSeries {
  clearSky: (number | null)[];
  weather: (number | null)[];
}

// The energyflow intervals carry a local position rather than an instant, so each installation
// slot is matched to the interval at the same position in the installation's zone. A week is
// numbered by ISO weekday, Monday being 1.
export function slotPosition(time: string, timezone: string, granularity: string): number | null {
  const local = moment.tz(time, timezone);
  switch (granularity) {
    case 'quarter-hour':
      return local.hours() * 4 + Math.floor(local.minutes() / 15);
    case 'hour':
      return local.hours();
    case 'week':
      return local.isoWeekday();
    case 'day':
      return local.date();
    case 'month':
      return local.month() + 1;
    default:
      return null;
  }
}

type IntervalPosition = Pick<PvForecastInterval, 'quarter_hour' | 'hour' | 'day' | 'month'>;

// The energyflow route numbers its hours 1 to 24, the first covering 00:00 to 01:00, while its
// quarter-hours start at 0.
function intervalPosition(interval: IntervalPosition): number | undefined {
  if (interval.hour !== undefined) {
    return interval.hour - 1;
  }
  return interval.quarter_hour ?? interval.day ?? interval.month;
}

// Every PV channel of the device is summed: the chart shows the forecast for the array the device
// meters, not per channel.
function sumOf(entries: PvForecastEntry[], field: 'clearSkyInWh' | 'weatherInWh'): number | null {
  const values = entries
    .map(entry => entry[field])
    .filter((value): value is number => value !== null);
  return values.length === 0 ? null : values.reduce((total, value) => total + value, 0);
}

// The interval at each slot of the response, null where the device has none.
export function intervalsBySlot<T extends IntervalPosition>(
  intervals: readonly T[] | null | undefined,
  telemetry: InstallationTelemetry,
): (T | null)[] {
  const byPosition = new Map<number, T>();
  for (const interval of intervals ?? []) {
    const position = intervalPosition(interval);
    if (position !== undefined && !byPosition.has(position)) {
      byPosition.set(position, interval);
    }
  }
  const slots = telemetry.points[0]?.slots ?? [];
  return slots.map(slot => {
    const position = slotPosition(slot.time, telemetry.timezone, telemetry.granularity);
    return position === null ? null : (byPosition.get(position) ?? null);
  });
}

export function alignPvForecast(
  forecast: DevicePvForecast | null | undefined,
  telemetry: InstallationTelemetry | null,
): PvForecastSeries | null {
  if (!forecast || !telemetry) {
    return null;
  }
  const entries = intervalsBySlot(forecast.intervals, telemetry).map(
    interval => interval?.pv ?? [],
  );
  const series = {
    clearSky: entries.map(entry => sumOf(entry, 'clearSkyInWh')),
    weather: entries.map(entry => sumOf(entry, 'weatherInWh')),
  };
  const hasValue = [...series.clearSky, ...series.weather].some(value => value !== null);
  return hasValue ? series : null;
}

export function seriesTotal(values: (number | null)[]): number | null {
  const present = values.filter((value): value is number => value !== null);
  return present.length === 0 ? null : present.reduce((total, value) => total + value, 0);
}

export interface ForecastComparison {
  measured: number;
  expected: number;
}

// Only the slots where both sides have a value are added up: the rest of today already has a
// forecast but no measurement yet, and a gap in the measurement has nothing to compare with.
export function forecastComparison(
  measured: (number | null)[],
  expected: (number | null)[],
): ForecastComparison | null {
  let compared = 0;
  const totals = { measured: 0, expected: 0 };
  for (const [index, value] of measured.entries()) {
    const forecast = expected[index] ?? null;
    if (value !== null && forecast !== null) {
      totals.measured += value;
      totals.expected += forecast;
      compared++;
    }
  }
  return compared === 0 ? null : totals;
}
